import { Event, useStore } from '@mirai/data-sources';
import { useLocale } from '@mirai/locale';
import { Action, Text, styles, useDevice, View } from '@mirai/ui';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { Amenities, Features, Slider } from './components';
import { L10N } from './Item.l10n';
import * as style from './Item.module.css';
import { EVENT, METRICS_EVENTS } from '../../../helpers';

const GAP_TABLET = 7;
const GAP_NOT_TABLET = 1;
let memoizedWidth = 374;

const Preview = ({
  availability = 0,
  cardImageHeight,
  features: { columnMode = false, ...features } = {},
  images = [],
  id,
  isSoldOut = false,
  media,
  name: title,
  resultPosition,
  showActions = true,
  width: propWidth,
  onMoreInfo,
  onTwin = () => {},
  onViewUrl = () => {},
  ...others
}) => {
  const { height, isMobile, isPortrait, isTablet, width } = useDevice();
  const { translate } = useLocale();

  const [, setTimestamp] = useState();

  const ref = useRef(null);
  const {
    value: {
      finder: { place: { id: [placeId] = [], isHotel } = {} } = {},
      hotel: { features: hotelFeatures = {}, id: hotelId, warningAvailability = 9999 } = {},
      id: instanceId,
    } = {},
  } = useStore();

  useEffect(() => {
    const { clientWidth } = ref.current || {};
    if (clientWidth && !propWidth) {
      memoizedWidth = clientWidth - (isTablet ? GAP_TABLET : GAP_NOT_TABLET);
      setTimestamp(Date.now());
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTablet, width]);

  const handleCardInteraction = (elementClicked) => {
    Event.publish(EVENT.METRICS, {
      id: METRICS_EVENTS.RATES_INTERACTION,
      action: 'card',
      element_clicked: elementClicked,
      result_position: resultPosition,
    });
  };

  const handleSliderCounter = () => {
    handleCardInteraction('photo');
    onMoreInfo && onMoreInfo();
  };

  const handleTwin = () => {
    Event.publish(EVENT.EXTERNAL_TWIN, { event: EVENT.EXTERNAL_TWIN, source: 'rates' });
    handleCardInteraction('3d');
    onTwin(id);
  };

  const { showMiraiTwin } =
    (placeId ? (isHotel ? hotelFeatures[placeId] : hotelFeatures[instanceId]) : hotelFeatures[hotelId]) || {};

  return (
    <View ref={ref} className={styles(style.preview, !showActions && style.withRadius)}>
      <Slider
        height={columnMode ? height / (isPortrait ? 5 : 3) : cardImageHeight}
        images={images.map(({ large }) => large)}
        media={showActions ? media : undefined}
        title={title}
        width={propWidth || memoizedWidth}
        onCounter={!isMobile ? handleSliderCounter : undefined}
        onPress={onMoreInfo ? handleSliderCounter : undefined}
        onViewUrl={onViewUrl}
        className={style.slider}
      />

      <View row className={[style.offset, style.spaceBetween, style.details]}>
        <Features {...{ features, ...others }} />

        {isSoldOut === false && availability <= warningAvailability && (
          <Text bold error small>
            {translate(L10N.LABEL_REMAINING_ITEMS, { amount: availability })}
          </Text>
        )}
      </View>

      <View className={[style.offset, style.gap]} style={{ flex: 1 }}>
        <Text bold brand headline>
          {title}
        </Text>

        {id && showMiraiTwin && showActions && (
          <Action onPress={handleTwin}>
            <Text bold small className={style.tag3D}>
              3D
            </Text>
            {translate(L10N.LABEL_MIRAI_TWIN)}
          </Action>
        )}

        <Amenities highlight preview {...others} />

        {showActions && (
          <View row>
            <Action
              onPress={() => {
                handleCardInteraction('more_info');
                onMoreInfo && onMoreInfo();
              }}
            >
              {translate(L10N.ACTION_MORE_INFO)}
            </Action>
          </View>
        )}
      </View>
    </View>
  );
};

Preview.displayName = 'Mirai:Core:Rates:Item:Preview';

Preview.propTypes = {
  availability: PropTypes.number,
  cardImageHeight: PropTypes.number,
  features: PropTypes.shape({
    columnMode: PropTypes.bool,
  }),
  images: PropTypes.arrayOf(
    PropTypes.shape({
      alt: PropTypes.string,
      large: PropTypes.string,
      thumbnail: PropTypes.string,
    }),
  ),
  id: PropTypes.number,
  isSoldOut: PropTypes.bool,
  media: PropTypes.shape({
    url3D: PropTypes.string,
    urlVideo: PropTypes.string,
  }),
  name: PropTypes.string,
  resultPosition: PropTypes.number,
  showActions: PropTypes.bool,
  width: PropTypes.number,
  onMoreInfo: PropTypes.func,
  onTwin: PropTypes.func,
  onViewUrl: PropTypes.func,
};

export { Preview };
